import React from "react";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import {Box, Typography} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import {CardBox} from "../Theme/CustomComponent";
import {TrackingResult} from "./TrackingResult";
import {TrackingTruckIllustration} from "./TrackingTruckIllustration";

const ResultAnimation = ({version, children}) => (
    <Box key={version} sx={{animation: "trackingResultIn 0.4s ease-out", "@keyframes trackingResultIn": {from: {opacity: 0, transform: "translateY(10px)"}, to: {opacity: 1, transform: "translateY(0)"}}, "@media (prefers-reduced-motion: reduce)": {animation: "none"}}}>
        {children}
    </Box>
);

const ReadyState = ({trackingType, truckVariant, transparentBackground}) => {
    const theme = useTheme();
    return (
        <CardBox sx={{p: {xs: 2.5, sm: 4, md: 5}, py: {xs: 6, sm: 8, md: 11}, minHeight: {xs: 300, sm: 380, md: 500}, borderRadius: `${theme.shape.lg}px`, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", backgroundColor: transparentBackground ? "transparent" : "background.paper", borderStyle: "dashed", borderColor: alpha(theme.palette.primary.main, 0.15)}}>
            <Box sx={{mb: 2, transform: "scale(1.2)"}}><TrackingTruckIllustration variant={truckVariant} muted /></Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{fontWeight: 600}}>Ready to Track</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, maxWidth: 360, mx: "auto"}}>
                {trackingType === "package" ? "Enter your package tracking number in the field above to retrieve real-time transit details." : "Enter your BOL, PRO, or pickup number in the field above to retrieve real-time transit details."}
            </Typography>
        </CardBox>
    );
};

export const TrackingContentState = ({isLoading, result, hasSearched, resultVersion, trackingType, truckVariant, historyLayout, transparentReadyState = false}) => {
    const theme = useTheme();
    if (isLoading) {
        return (
            <CardBox aria-live="polite" sx={{p: {xs: 3, md: 4}, borderRadius: `${theme.shape.lg}px`, textAlign: "center"}}>
                <TrackingTruckIllustration variant={truckVariant} animated />
                <Typography variant="body2" sx={{mt: 1, color: "text.secondary"}}>Fetching live tracking status...</Typography>
            </CardBox>
        );
    }
    if (result) {
        return <ResultAnimation version={resultVersion}><TrackingResult result={result} historyLayout={historyLayout} truckVariant={truckVariant} /></ResultAnimation>;
    }
    if (!hasSearched) return <ReadyState trackingType={trackingType} truckVariant={truckVariant} transparentBackground={transparentReadyState} />;

    return (
        <ResultAnimation version={resultVersion}>
            <CardBox role="status" sx={{p: 3, textAlign: "center", borderRadius: `${theme.shape.lg}px`}}>
                <SearchOffRoundedIcon aria-hidden="true" sx={{fontSize: 24, color: "neutral.400", mb: 0.75}} />
                <Typography variant="h4">No tracking data found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>Check the number and try again.</Typography>
            </CardBox>
        </ResultAnimation>
    );
};
